import { useLocation, useSearchParams } from "react-router";

/**
 * The periods a page of statistics offers (`<PeriodSelect>`, the presets of
 * `<TimeRangeFilter>`), in calendar days in Belgian time, today included: the
 * past week, month and quarter. The first is the default. A page keeps its
 * period in the URL as `?days=30`, left out at the default.
 */
export const PERIODS = [7, 30, 90] as const;
export type Period = (typeof PERIODS)[number];

/**
 * The period in a `?days=` search parameter, or the page's default for
 * anything else: the first period, unless the page opens on another (the
 * Logboek opens on the past month).
 */
export function parsePeriod(value: string | null, fallback: Period = PERIODS[0]): Period {
  return PERIODS.find((period) => String(period) === value) ?? fallback;
}

/** Whether a number of days is one of the periods. */
export function isPeriod(value: unknown): value is Period {
  return PERIODS.some((period) => period === value);
}

const PERIOD_KEYS = { 7: "week", 30: "month", 90: "quarter" } as const satisfies Record<
  Period,
  string
>;

/** A period's key in the messages, for its name: `period.last.week` for 7 days. */
export function periodKey(period: Period): (typeof PERIOD_KEYS)[Period] {
  return PERIOD_KEYS[period];
}

/**
 * A page's period kept in its URL (`?days=30`, left out at the default),
 * replacing the history entry as the filters do and leaving every other
 * parameter and the entry's state (an issue page's way back to its list),
 * for a page with a `<PeriodSelect>`.
 */
export function usePeriod(): [Period, (period: Period) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  return [
    parsePeriod(params.get("days")),
    (period) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete("from");
          next.delete("to");
          if (period === PERIODS[0]) {
            next.delete("days");
          } else {
            next.set("days", String(period));
          }
          return next;
        },
        { replace: true, state },
      ),
  ];
}
